import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  Shield, Smartphone, Mail, Key, CheckCircle, AlertTriangle,
  Copy, Download, Loader2, ArrowRight, X, QrCode
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ===================== MFA SETUP COMPONENT =====================
export const MFASetup = ({ onComplete }) => {
  const [step, setStep] = useState('start'); // start, qr, verify, backup, done
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const startSetup = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_URL}/api/auth/mfa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetupData(data);
      setStep('qr');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (verifyCode.length !== 6) {
      setError('Enter a 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/auth/mfa/verify-setup`, 
        { code: verifyCode, method: 'totp' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStep('backup');
      toast.success('MFA enabled successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    if (setupData?.backup_codes) {
      navigator.clipboard.writeText(setupData.backup_codes.join('\n'));
      toast.success('Backup codes copied to clipboard');
    }
  };

  if (step === 'start') {
    return (
      <Card className="border-2 border-dashed border-emerald-200 dark:border-emerald-800" data-testid="mfa-setup-start">
        <CardContent className="p-6 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Enable Two-Factor Authentication</h3>
          <p className="text-sm text-slate-400 mb-6">Add an extra layer of security with authenticator app + email verification</p>
          <Button onClick={startSetup} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mfa-enable-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
            Enable MFA
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (step === 'qr') {
    return (
      <Card data-testid="mfa-qr-step">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-emerald-500" />
            Scan QR Code
          </CardTitle>
          <CardDescription>Open your authenticator app (Google Authenticator, Authy) and scan this code</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-center">
            <div className="bg-white p-4 rounded-xl border">
              <img src={setupData?.qr_code} alt="MFA QR Code" className="w-48 h-48" />
            </div>
          </div>
          
          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
            <p className="text-xs text-slate-400 mb-1">Or enter this key manually:</p>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono bg-white dark:bg-slate-900 px-3 py-1.5 rounded border flex-1 break-all">
                {setupData?.secret}
              </code>
              <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(setupData?.secret); toast.success('Secret copied'); }}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Enter the 6-digit code from your app:</p>
            <div className="flex gap-2">
              <Input
                value={verifyCode}
                onChange={(e) => { setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                placeholder="000000"
                className="text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                data-testid="mfa-setup-code-input"
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setStep('start'); setSetupData(null); }} className="flex-1">Cancel</Button>
            <Button onClick={verifySetup} disabled={loading || verifyCode.length !== 6} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="mfa-verify-setup-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Verify & Enable
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'backup') {
    return (
      <Card data-testid="mfa-backup-step">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-500" />
            Save Your Backup Codes
          </CardTitle>
          <CardDescription>
            <span className="text-amber-600 font-medium">Save these codes somewhere safe.</span> If you lose access to your authenticator app, you can use these to login.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2 bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
            {setupData?.backup_codes?.map((code, i) => (
              <div key={i} className="font-mono text-sm bg-white dark:bg-slate-900 px-3 py-2 rounded border text-center">
                {code}
              </div>
            ))}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyBackupCodes} className="flex-1" data-testid="copy-backup-btn">
              <Copy className="w-4 h-4 mr-2" />
              Copy Codes
            </Button>
            <Button variant="outline" onClick={() => {
              const text = `CARTIEAI Backup Codes\n${'='.repeat(30)}\n${setupData?.backup_codes?.join('\n')}`;
              const blob = new Blob([text], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = 'cartie-ai-backup-codes.txt'; a.click();
            }} className="flex-1">
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>

          <Button onClick={() => { setStep('done'); if (onComplete) onComplete(); }} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="mfa-done-btn">
            I've Saved My Codes
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-emerald-200 dark:border-emerald-800" data-testid="mfa-enabled-card">
      <CardContent className="p-6 text-center">
        <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-slate-900 dark:text-white">MFA Enabled</h3>
        <p className="text-sm text-slate-400 mt-1">Your account is protected with two-factor authentication</p>
      </CardContent>
    </Card>
  );
};

// ===================== MFA LOGIN VERIFICATION =====================
export const MFAVerification = ({ mfaToken, mfaMethods, emailHint, onSuccess, onCancel }) => {
  const [method, setMethod] = useState(mfaMethods?.includes('totp') ? 'totp' : 'email');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [method]);

  const handleVerify = async () => {
    if (code.length < 6) {
      setError('Enter a 6-digit code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/mfa/verify`,
        { code, method },
        { headers: { 'X-MFA-Token': mfaToken } }
      );
      onSuccess(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendEmailOTP = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/mfa/send-email-otp`, {}, {
        headers: { 'X-MFA-Token': mfaToken }
      });
      setEmailSent(true);
      toast.success('Verification code sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send code');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleVerify();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="mfa-verify-modal">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-2">
            <Shield className="w-7 h-7 text-emerald-600" />
          </div>
          <CardTitle>Two-Factor Verification</CardTitle>
          <CardDescription>Enter the code to continue signing in</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Method Toggle */}
          {mfaMethods?.length > 1 && (
            <div className="flex rounded-lg border overflow-hidden">
              <button
                onClick={() => { setMethod('totp'); setCode(''); setError(''); }}
                className={`flex-1 px-3 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                  method === 'totp' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'text-slate-400 hover:bg-slate-50'
                }`}
                data-testid="mfa-totp-tab"
              >
                <Smartphone className="w-4 h-4" />
                Authenticator
              </button>
              <button
                onClick={() => { setMethod('email'); setCode(''); setError(''); if (!emailSent) sendEmailOTP(); }}
                className={`flex-1 px-3 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                  method === 'email' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'text-slate-400 hover:bg-slate-50'
                }`}
                data-testid="mfa-email-tab"
              >
                <Mail className="w-4 h-4" />
                Email Code
              </button>
            </div>
          )}

          {/* Instructions */}
          <p className="text-sm text-slate-400 text-center">
            {method === 'totp' 
              ? 'Enter the 6-digit code from your authenticator app' 
              : `Enter the code sent to ${emailHint}`}
          </p>

          {/* Code Input */}
          <Input
            ref={inputRef}
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
            onKeyDown={handleKeyDown}
            placeholder="000000"
            className="text-center text-3xl tracking-[0.6em] font-mono h-14"
            maxLength={6}
            autoComplete="one-time-code"
            data-testid="mfa-code-input"
          />
          
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Resend email OTP */}
          {method === 'email' && emailSent && (
            <button onClick={sendEmailOTP} className="text-xs text-emerald-600 hover:underline w-full text-center">
              Didn't receive it? Send again
            </button>
          )}

          {/* Backup code hint */}
          {method === 'totp' && (
            <p className="text-xs text-slate-400 text-center">
              Lost your authenticator? Enter a backup code instead
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onCancel} className="flex-1" data-testid="mfa-cancel-btn">
              Cancel
            </Button>
            <Button 
              onClick={handleVerify} 
              disabled={loading || code.length < 6} 
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              data-testid="mfa-verify-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Verify
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
